import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip, TablePagination,
  Accordion, AccordionSummary, AccordionDetails, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../api/client.js';
import type { IAuditLogEntry } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';

export default function AuditLogPage() {
  const [entries, setEntries] = useState<IAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (entityType) params.set('entity_type', entityType);
    if (action) params.set('action', action);

    const res = await api.get<{ entries: IAuditLogEntry[]; total: number }>(`/admin/audit-log?${params}`);
    if (res.success) {
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } else {
      setError(res.error.message);
    }
  };

  useEffect(() => { loadData(); }, [page, rowsPerPage, entityType, action]);

  const actionColor = (a: string): string => {
    if (a === 'CREATE') return colors.scoreGreen;
    if (a === 'UPDATE') return colors.scoreYellow;
    if (a === 'DELETE') return colors.scoreRed;
    return colors.black60;
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Audit Log</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select value={entityType} label="Entity Type" onChange={(e) => { setEntityType(e.target.value); setPage(0); }}>
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="categories">categories</MenuItem>
            <MenuItem value="checks">checks</MenuItem>
            <MenuItem value="mapping_rules">mapping_rules</MenuItem>
            <MenuItem value="quick_wins">quick_wins</MenuItem>
            <MenuItem value="disclaimer">disclaimer</MenuItem>
            <MenuItem value="users">users</MenuItem>
            <MenuItem value="app_settings">app_settings</MenuItem>
            <MenuItem value="cron_jobs">cron_jobs</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Action</InputLabel>
          <Select value={action} label="Action" onChange={(e) => { setAction(e.target.value); setPage(0); }}>
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="CREATE">CREATE</MenuItem>
            <MenuItem value="UPDATE">UPDATE</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>Zeitpunkt</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Diff</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(entry.created_at).toLocaleString('de-DE')}</TableCell>
                <TableCell>{entry.user_email}</TableCell>
                <TableCell>
                  <Chip label={entry.action} size="small" sx={{ bgcolor: actionColor(entry.action), color: '#fff', fontWeight: 600 }} />
                </TableCell>
                <TableCell>
                  <Chip label={entry.entity_type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{entry.entity_id ?? '—'}</TableCell>
                <TableCell>
                  {entry.diff && (
                    <Accordion elevation={0} disableGutters sx={{ bgcolor: 'transparent' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, minHeight: 'auto' }}>
                        <Typography variant="body2" color="text.secondary">Details</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1 }}>
                        <Box sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
                          <pre>{JSON.stringify(entry.diff, null, 2)}</pre>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
}
